/**
 * Last-ditch React error boundary.
 *
 * Without this, an exception inside any child component bubbles up
 * to React's default handler and the host page is left in a broken
 * state — on phones the browser then often discards the tab and
 * "reloads" out from under the user. We catch those errors and
 * render a small recovery banner so the user keeps their session
 * (sealed keystore is still unlocked, etc.) and can retry without
 * losing their place.
 */
import { Component, type ErrorInfo, type ReactNode } from 'react';

export interface ErrorBoundaryProps {
  readonly children: ReactNode;
}

interface ErrorBoundaryState {
  readonly error: Error | undefined;
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  override state: ErrorBoundaryState = { error: undefined };

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    console.error('Chat render crashed', error, info);
  }

  reset = (): void => {
    this.setState({ error: undefined });
  };

  override render(): ReactNode {
    if (this.state.error) {
      // V5-LOW-08: error.message can echo user-controlled data
      // (filenames, peer userIds, payload fields). Render a generic
      // banner; the raw message is hidden behind a <details>
      // disclosure so curious operators can still inspect it but
      // it doesn't surface in screenshots / screen-share by default.
      return (
        <div className="error-boundary" data-testid="error-boundary" role="alert">
          <h2>Something went wrong</h2>
          <p className="faint">
            The page hit an unexpected error. Your session is intact — try again, or reload if the
            problem persists.
          </p>
          <details className="error-details">
            <summary>technical details</summary>
            <pre data-testid="error-boundary-message">{this.state.error.message}</pre>
          </details>
          <div className="hstack">
            <button type="button" className="primary" onClick={this.reset}>
              try again
            </button>
            <button
              type="button"
              className="ghost"
              onClick={() => {
                if (typeof window !== 'undefined') window.location.reload();
              }}
            >
              reload page
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}
